// src/api.ts
import { auth } from './firebase'

const LS_BACKEND_TOKEN = 'eucamd.backend.jwt'

const decodeJwtExp = (token?: string | null) => {
  if (!token) return null
  const parts = token.split('.')
  if (parts.length < 2) return null
  try {
    const payload = JSON.parse(atob(parts[1]))
    const expSec = Number(payload?.exp)
    return Number.isFinite(expSec) ? expSec * 1000 : null
  } catch {
    return null
  }
}

let lastToken: string | null = null
let lastTokenExpMs: number | null = null

async function fetchAuthToken(forceRefresh = false): Promise<string | null> {
  const u = auth.currentUser
  if (u) {
    try {
      let tok = await u.getIdToken(forceRefresh)
      const expMs = decodeJwtExp(tok)
      // Force refresh if token expires within 5 minutes
      if (!forceRefresh && expMs && expMs - Date.now() < 5 * 60 * 1000) {
        tok = await u.getIdToken(true)
        lastTokenExpMs = decodeJwtExp(tok)
      } else {
        lastTokenExpMs = expMs
      }
      lastToken = tok
      return tok
    } catch {
      // If Firebase token fetch glitches, reuse last known token if still valid.
      if (lastToken && lastTokenExpMs && lastTokenExpMs - Date.now() > 30 * 1000) {
        return lastToken
      }
      // fall through to backend token
    }
  }
  // Fallback: backend-issued JWT from magic link or /auth/google/start flow
  try {
    if (typeof window !== 'undefined') {
      const tok = window.localStorage.getItem(LS_BACKEND_TOKEN)
      if (tok) return tok
    }
  } catch {
    // ignore storage errors
  }
  return null
}

async function authHeader(forceRefresh = false) {
  const token = await fetchAuthToken(forceRefresh)
  if (!token) return {}
  const needsBearer = !token.startsWith('EUCA ')
  return { Authorization: needsBearer ? `Bearer ${token}` : token }
}

const toHeaderRecord = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) return {}
  const out: Record<string, string> = {}
  try {
    const normalized = new Headers(headers)
    normalized.forEach((value, key) => {
      out[key] = value
    })
    return out
  } catch {
    if (Array.isArray(headers)) {
      for (const [key, value] of headers) out[String(key)] = String(value)
      return out
    }
    for (const [key, value] of Object.entries(headers as Record<string, unknown>)) {
      if (value == null) continue
      out[key] = String(value)
    }
    return out
  }
}

export async function fetchAuthTokenForRealtime(forceRefresh = false): Promise<string | null> {
  return fetchAuthToken(forceRefresh)
}

export type ProviderProfession =
  | 'doctor'
  | 'education'
  | 'tech'
  | 'property'
  | 'financial'
  | 'allied'
  | 'legal'
  | 'migration'
export const hasActiveProviderProfile = (profile?: any) => {
  if (!profile || profile.needsRegistration) return false
  const status = String(profile.status || profile.approvalStatus || '').trim().toUpperCase()
  if (status) {
    if (status === 'NONE' || status === 'PENDING' || status === 'REJECTED') return false
    return true
  }
  return Array.isArray(profile.specialties) && profile.specialties.length > 0
}
let cachedDoctorProfile: any = null
let cachedEducationProfile: any = null
let cachedTechProfile: any = null
let cachedFinancialProfile: any = null
let cachedPropertyProfile: any = null
let cachedAlliedProfile: any = null
let cachedLegalProfile: any = null
let cachedMigrationProfile: any = null
const enabledStackList: ProviderProfession[] = (() => {
  const raw = String(import.meta.env.VITE_ENABLED_STACKS || '')
  const fromEnv = raw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)

  const fallback: string[] = []
  if (!fromEnv.length) {
    fallback.push('doctor')
    const hasEducationApi =
      Boolean(import.meta.env.VITE_EDUCATION_API_BASE) ||
      Boolean(import.meta.env.VITE_API_EDUCATION_BASE)
    if (hasEducationApi) fallback.push('education')
    const hasTechApi =
      Boolean(import.meta.env.VITE_TECH_API_BASE) ||
      Boolean(import.meta.env.VITE_API_TECH_BASE)
    if (hasTechApi) fallback.push('tech')
    const hasPropertyApi =
      Boolean(import.meta.env.VITE_PROPERTY_API_BASE) ||
      Boolean(import.meta.env.VITE_API_PROPERTY_BASE)
    if (hasPropertyApi) fallback.push('property')
    const hasFinancialApi =
      Boolean(import.meta.env.VITE_FINANCIAL_API_BASE) ||
      Boolean(import.meta.env.VITE_API_FINANCIAL_BASE)
    if (hasFinancialApi) fallback.push('financial')
    const hasAlliedApi =
      Boolean(import.meta.env.VITE_ALLIED_API_BASE) ||
      Boolean(import.meta.env.VITE_API_ALLIED_BASE)
    if (hasAlliedApi) fallback.push('allied')
    const hasLegalApi =
      Boolean(import.meta.env.VITE_LEGAL_API_BASE) ||
      Boolean(import.meta.env.VITE_API_LEGAL_BASE)
    if (hasLegalApi) fallback.push('legal')
    const hasMigrationApi =
      Boolean(import.meta.env.VITE_MIGRATION_API_BASE) ||
      Boolean(import.meta.env.VITE_API_MIGRATION_BASE)
    if (hasMigrationApi) fallback.push('migration')
  }

  const mapped = new Set<ProviderProfession>()
  const source = fromEnv.length ? fromEnv : fallback
  for (const part of source) {
    if (part === 'doctor') mapped.add('doctor')
    else if (part === 'education') mapped.add('education')
    else if (part === 'tech') mapped.add('tech')
    else if (part === 'property') mapped.add('property')
    else if (part === 'financial') mapped.add('financial')
    else if (part === 'allied') mapped.add('allied')
    else if (part === 'legal') mapped.add('legal')
    else if (part === 'migration') mapped.add('migration')
    else mapped.add('doctor')
  }
  if (!mapped.size) mapped.add('doctor')
  return Array.from(mapped)
})()
const enabledStackSet = new Set(enabledStackList)
const enabledStackArray: ProviderProfession[] =
  enabledStackList.length ? enabledStackList : ['doctor']

const normalizeStack = (raw: string | null): ProviderProfession | null => {
  if (!raw) return null
  const val = raw.trim().toLowerCase()
  if (!val) return null
  return enabledStackSet.has(val as ProviderProfession) ? (val as ProviderProfession) : null
}

const stackFromLocation = (): ProviderProfession | null => {
  if (typeof window === 'undefined') return null
  const sp = new URLSearchParams(window.location.search)
  const direct = normalizeStack(sp.get('stack'))
  if (direct) return direct
  const next = sp.get('next')
  if (next && next.startsWith('/')) {
    try {
      const nextUrl = new URL(next, window.location.origin)
      const fromNext = normalizeStack(nextUrl.searchParams.get('stack'))
      if (fromNext) return fromNext
    } catch {
      // ignore malformed next param
    }
  }
  return null
}

const stackFallbackOrder = (): ProviderProfession[] => {
  const primary = stackFromLocation()
  if (!primary) return enabledStackArray
  return [primary, ...enabledStackArray.filter((s) => s !== primary)]
}

const buildProviderMeFallback = async () => {
  const order = stackFallbackOrder()
  for (const stack of order) {
    try {
      const profile = await getCachedProviderProfile(stack).catch(() => null)
      if (!profile || !hasActiveProviderProfile(profile)) continue
      const roles = Array.isArray(profile?.roles) ? profile.roles.slice() : []
      if (!roles.includes('specialist')) roles.push('specialist')
      return {
        ...profile,
        role: 'specialist',
        roles,
        providerStack: stack,
      }
    } catch {
      // ignore and try next stack
    }
  }
  return null
}

const API_BASE = import.meta.env.VITE_API_BASE
const DOCTOR_API_BASE =
  import.meta.env.VITE_DOCTOR_API_BASE || import.meta.env.VITE_API_DOCTOR_BASE || API_BASE
const EDUCATION_API_BASE =
  import.meta.env.VITE_EDUCATION_API_BASE || import.meta.env.VITE_API_EDUCATION_BASE || DOCTOR_API_BASE
const TECH_API_BASE =
  import.meta.env.VITE_TECH_API_BASE || import.meta.env.VITE_API_TECH_BASE || DOCTOR_API_BASE
const PROPERTY_API_BASE =
  import.meta.env.VITE_PROPERTY_API_BASE || import.meta.env.VITE_API_PROPERTY_BASE || DOCTOR_API_BASE
const FINANCIAL_API_BASE =
  import.meta.env.VITE_FINANCIAL_API_BASE || import.meta.env.VITE_API_FINANCIAL_BASE || DOCTOR_API_BASE
const ALLIED_API_BASE =
  import.meta.env.VITE_ALLIED_API_BASE || import.meta.env.VITE_API_ALLIED_BASE || DOCTOR_API_BASE
const LEGAL_API_BASE =
  import.meta.env.VITE_LEGAL_API_BASE || import.meta.env.VITE_API_LEGAL_BASE || DOCTOR_API_BASE
const MIGRATION_API_BASE =
  import.meta.env.VITE_MIGRATION_API_BASE || import.meta.env.VITE_API_MIGRATION_BASE || DOCTOR_API_BASE

/** Single fetch helper with lightweight retry on network-ish errors. */
type RequestOptions = {
  skipAuthSignOut?: boolean
}

async function request(path: string, options: RequestInit = {}, reqOptions: RequestOptions = {}) {
  const method = (options.method || 'GET').toString().toUpperCase()
  const isGet = method === 'GET'
  const hdr = await authHeader()
  const url = `${API_BASE}${path}`

  // Only add Content-Type if body is present
  const baseHeaders: Record<string, string> = {
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...toHeaderRecord(options.headers),
    ...hdr,
  }

  const init: RequestInit = {
    ...options,
    headers: baseHeaders,
    credentials: 'omit',
  }

  let lastErr: any
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const res = await fetch(url, init)
      if (!res.ok) {
        // If auth failed, try a forced refresh once
        if (res.status === 401 || res.status === 403) {
          if (attempt === 0) {
            init.headers = {
              ...(options.body ? { 'Content-Type': 'application/json' } : {}),
              ...toHeaderRecord(options.headers),
              ...(await authHeader(true)),
            }
            continue
          }
          if (!reqOptions.skipAuthSignOut) {
            const authHeaderValue =
              (init.headers as any)?.Authorization || (init.headers as any)?.authorization
            if (authHeaderValue) {
              try { await auth.signOut() } catch {}
            }
          }
        }
        // Soft retry for transient server errors on GETs
        if (isGet && attempt === 0 && (res.status === 429 || res.status >= 500)) {
          await new Promise(r => setTimeout(r, 450))
          continue
        }
        let msg = res.statusText
        try {
          msg = (await res.json())?.error || msg
        } catch {
          try { msg = await res.text() } catch {}
        }
        throw new Error(msg)
      }
      // Some endpoints may be 204
      const text = await res.text()
      return text ? JSON.parse(text) : {}
    } catch (e: any) {
      lastErr = e
      const maybeNetwork =
        e?.name === 'TypeError' || String(e?.message || '').toLowerCase().includes('network')
      if (isGet && maybeNetwork && attempt === 0) {
        await new Promise(r => setTimeout(r, 400))
        continue
      }
      break
    }
  }
  throw lastErr
}

async function stackRequest(baseUrl: string | undefined, path: string, options: RequestInit = {}) {
  const method = (options.method || 'GET').toString().toUpperCase()
  const isGet = method === 'GET'
  if (!baseUrl) throw new Error('Missing API base URL')
  const hdr = await authHeader()
  const url = `${baseUrl}${path}`
  const baseHeaders: Record<string, string> = {
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...toHeaderRecord(options.headers),
    ...hdr,
  }
  const init: RequestInit = {
    ...options,
    headers: baseHeaders,
    credentials: 'omit',
  }

  let lastErr: any
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const res = await fetch(url, init)
      if (!res.ok) {
        if (res.status === 400 && path.includes('/selections/incoming')) {
          return { items: [] }
        }
        if (res.status === 401 || res.status === 403) {
          if (attempt === 0) {
            init.headers = {
              ...(options.body ? { 'Content-Type': 'application/json' } : {}),
              ...toHeaderRecord(options.headers),
              ...(await authHeader(true)),
            }
            continue
          }
          const authHeaderValue =
            (init.headers as any)?.Authorization || (init.headers as any)?.authorization
          if (authHeaderValue) {
            try { await auth.signOut() } catch {}
          }
        }
        if (isGet && attempt === 0 && (res.status === 429 || res.status >= 500)) {
          await new Promise((r) => setTimeout(r, 450))
          continue
        }
        let msg = res.statusText
        try {
          msg = (await res.json())?.error || msg
        } catch {
          try {
            msg = await res.text()
          } catch {}
        }
        throw new Error(msg)
      }
      const text = await res.text()
      return text ? JSON.parse(text) : {}
    } catch (e: any) {
      lastErr = e
      const maybeNetwork =
        e?.name === 'TypeError' || String(e?.message || '').toLowerCase().includes('network')
      if (isGet && maybeNetwork && attempt === 0) {
        await new Promise((r) => setTimeout(r, 400))
        continue
      }
      break
    }
  }
  throw lastErr
}

async function doctorRequest(path: string, options: RequestInit = {}) {
  return stackRequest(DOCTOR_API_BASE, path, options)
}

async function educationRequest(path: string, options: RequestInit = {}) {
  return stackRequest(EDUCATION_API_BASE, path, options)
}

async function techRequest(path: string, options: RequestInit = {}) {
  return stackRequest(TECH_API_BASE, path, options)
}

async function propertyRequest(path: string, options: RequestInit = {}) {
  return stackRequest(PROPERTY_API_BASE, path, options)
}

async function financialRequest(path: string, options: RequestInit = {}) {
  return stackRequest(FINANCIAL_API_BASE, path, options)
}

async function alliedRequest(path: string, options: RequestInit = {}) {
  return stackRequest(ALLIED_API_BASE, path, options)
}

async function legalRequest(path: string, options: RequestInit = {}) {
  return stackRequest(LEGAL_API_BASE, path, options)
}

async function migrationRequest(path: string, options: RequestInit = {}) {
  return stackRequest(MIGRATION_API_BASE, path, options)
}

const stackRequestByProfession = (profession: ProviderProfession) => {
  switch (profession) {
    case 'education':
      return educationRequest
    case 'tech':
      return techRequest
    case 'property':
      return propertyRequest
    case 'financial':
      return financialRequest
    case 'allied':
      return alliedRequest
    case 'legal':
      return legalRequest
    case 'migration':
      return migrationRequest
    default:
      return doctorRequest
  }
}

async function ensureActiveProviderProfile(profession: ProviderProfession) {
  // Skip provider lookups when no signed-in user; avoids unauthenticated /work/* calls
  if (!auth.currentUser) return null
  const profile =
    profession === 'education'
      ? cachedEducationProfile
      : profession === 'tech'
        ? cachedTechProfile
        : profession === 'property'
          ? cachedPropertyProfile
          : profession === 'financial'
            ? cachedFinancialProfile
            : profession === 'allied'
              ? cachedAlliedProfile
              : profession === 'legal'
                ? cachedLegalProfile
                : profession === 'migration'
                  ? cachedMigrationProfile
                  : cachedDoctorProfile
  if (hasActiveProviderProfile(profile)) return profile
  const refreshed = await getCachedProviderProfile(profession)
  return hasActiveProviderProfile(refreshed) ? refreshed : null
}

export function cacheProviderProfile(profession: ProviderProfession, profile: any) {
  const normalized = profile || null
  switch (profession) {
    case 'education':
      cachedEducationProfile = normalized
      break
    case 'tech':
      cachedTechProfile = normalized
      break
    case 'property':
      cachedPropertyProfile = normalized
      break
    case 'financial':
      cachedFinancialProfile = normalized
      break
    case 'allied':
      cachedAlliedProfile = normalized
      break
    case 'legal':
      cachedLegalProfile = normalized
      break
    case 'migration':
      cachedMigrationProfile = normalized
      break
    default:
      cachedDoctorProfile = normalized
  }
}

async function getCachedProviderProfile(profession: ProviderProfession) {
  // Do not hit authenticated provider endpoints unless a user is signed in
  if (!auth.currentUser) return null
  if (profession === 'education') {
    if (cachedEducationProfile) return cachedEducationProfile
    cachedEducationProfile = await educationRequest('/work/education/me').catch(() => null)
    return cachedEducationProfile
  }
  if (profession === 'tech') {
    if (cachedTechProfile) return cachedTechProfile
    cachedTechProfile = await techRequest('/work/tech/me').catch(() => null)
    return cachedTechProfile
  }
  if (profession === 'property') {
    if (cachedPropertyProfile) return cachedPropertyProfile
    cachedPropertyProfile = await propertyRequest('/work/property/me').catch(() => null)
    return cachedPropertyProfile
  }
  if (profession === 'financial') {
    if (cachedFinancialProfile) return cachedFinancialProfile
    cachedFinancialProfile = await financialRequest('/work/financial/me').catch(() => null)
    return cachedFinancialProfile
  }
  if (profession === 'legal') {
    if (cachedLegalProfile) return cachedLegalProfile
    cachedLegalProfile = await legalRequest('/work/legal/me').catch(() => null)
    return cachedLegalProfile
  }
  if (profession === 'migration') {
    if (cachedMigrationProfile) return cachedMigrationProfile
    cachedMigrationProfile = await migrationRequest('/work/migration/me').catch(() => null)
    return cachedMigrationProfile
  }
  if (profession === 'allied') {
    if (cachedAlliedProfile) return cachedAlliedProfile
    cachedAlliedProfile = await alliedRequest('/work/allied/me').catch(() => null)
    return cachedAlliedProfile
  }
  if (cachedDoctorProfile) return cachedDoctorProfile
  cachedDoctorProfile = await doctorRequest('/work/doctors/me').catch(() => null)
  return cachedDoctorProfile
}

/* -------------------------
   Types (lightweight)
------------------------- */
export type AvailabilityPutBody = {
  /** canonical availability blocks in UTC ISO (server remains source of truth) */
  blocks: Array<{ startISO: string; endISO: string }>
  specialistId?: string
}

export type SelectionItem = {
  specialistId: string
  slotISO: string
}

export type CreateSelectionBody = {
  specialty: string
  picks?: SelectionItem[]
  reason?: string
  clientTimeZone?: string
  autoNotifyTop?: boolean
  state?: string
  feeCapCents?: number | null
}

export type GuestSelectionBody = CreateSelectionBody & {
  firstName: string
  lastName: string
  email: string
  phone?: string
  turnstileToken: string
}

export type SelectionSummary = {
  selectionId: string
  createdAt: string
  specialty: string
  reason?: string
  booking?: {
    specialistId: string
    slotISO: string
    feeCents: number
    paymentId: string
    createdAt: string
    status?: 'CANCELLED' | 'CONFIRMED'
  } | null
  payment?: {
    bookingFeeCents?: number
    paymentMethodId?: string
    stripeCustomerId?: string
    paymentId?: string
    status?: string
  } | null
  candidates: Array<{
    specialistId: string
    status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'LOST' | 'CANCELLED'
    slotISO: string
    feeCents?: number | null
    decisionAt?: string | null
  }>
}

export type ChatAttachment = {
  key: string
  name?: string | null
  type?: string | null
  size?: number | null
  uploadedAt?: string | null
}

export type ChatMessage = {
  messageId: string
  conversationId?: string | null
  senderId?: string | null
  recipientId?: string | null
  text?: string
  messageType?: string | null
  attachments?: ChatAttachment[]
  booking?: {
    slotISO?: string
    status?: string
    note?: string
    selectionId?: string
  } | null
  sentAt?: string | null
  readAt?: string | null
  status?: 'SENT' | 'READ' | string
}

export type ChatConversation = {
  conversationId: string
  patientId?: string | null
  specialistId?: string | null
  participantId?: string | null
  participantRole?: 'patient' | 'specialist' | string
  peerId?: string | null
  peerName?: string | null
  peerEmail?: string | null
  peerPhone?: string | null
  lastMessage?: string | null
  lastMessageType?: string | null
  lastMessageAt?: string | null
  unreadCount?: number
  selectionId?: string | null
}

export type StripeIdentityStatusResponse = {
  status: 'not_started' | 'checkout_open' | 'fee_pending' | 'pending' | 'requires_input' | 'verified' | 'failed' | string
  verified: boolean
  verifiedAt?: string | null
  hasUploadedId?: boolean
  checkoutOpen?: boolean
  fee?: {
    amountCents?: number
    currency?: string
    display?: string
    countryCode?: string
    statement?: string
  }
}

export async function stripeIdentityStatus(countryCode?: string): Promise<StripeIdentityStatusResponse> {
  const qp = new URLSearchParams()
  if (countryCode) qp.set('countryCode', countryCode)
  const qs = qp.toString()
  return request(`/stripe/identity/status${qs ? `?${qs}` : ''}`, { method: 'GET' })
}

export async function stripeIdentityStart(payload: {
  returnTo?: string
  countryCode?: string
} = {}) {
  return request('/stripe/identity/start', {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  })
}

export async function postMatchShortlist(body: {
  specialty: string
  timeframe?: '<72h' | '<1w' | '1-4w' | '>1m' | string
  mode?: 'either' | 'inperson' | 'telehealth' | string
  feeRange?: string
  reason?: string
}) {
  const timeframeMap: Record<string, string> = {
    '<72h': 'UNDER_72_HOURS',
    '<1w': 'UNDER_WEEK',
    '1-4w': 'ONE_TO_FOUR_WEEKS',
    '>1m': 'OVER_MONTH',
  }
  const modeMap: Record<string, string> = {
    either: 'EITHER',
    inperson: 'IN_PERSON',
    telehealth: 'TELEHEALTH',
  }
  const feeRange = String(body?.feeRange || '').trim()
  let feeCapCents: number | null | undefined = undefined
  if (feeRange && !feeRange.endsWith('+')) {
    const parts = feeRange.split('-').map((x) => Number(String(x).trim()))
    const upper = parts[1]
    if (Number.isFinite(upper)) feeCapCents = Math.round(upper * 100)
  } else if (feeRange.endsWith('+')) {
    feeCapCents = null
  }
  const payload: Record<string, any> = {
    specialty: String(body?.specialty || '').trim(),
    reason: String(body?.reason || '').trim(),
    timeframe: timeframeMap[String(body?.timeframe || '')] || String(body?.timeframe || ''),
    mode: modeMap[String(body?.mode || '').toLowerCase()] || String(body?.mode || ''),
  }
  if (feeCapCents !== undefined) payload.feeCapCents = feeCapCents
  return doctorRequest('/match/shortlist', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

/* -------------------------
   Convenience fallbacks
------------------------- */

/** Fallback if GET /selections/:id isn’t on the backend yet. */
async function getSelectionFromMine(selectionId: string) {
  const res = await doctorRequest('/selections/mine')
  const item = (res?.items || []).find((x: any) => x.selectionId === selectionId)
  if (!item) throw new Error('Selection not found')
  return item
}

/* -------------------------
   Legacy endpoints (still used)
------------------------- */
export const api = {
  me: async () => {
    try {
      return await request('/auth/me')
    } catch (err: any) {
      const tok = await fetchAuthToken()
      if (!tok || !tok.startsWith('EUCA ')) throw err
      const fallback = await buildProviderMeFallback()
      if (fallback) return fallback
      throw err
    }
  },
  exchangeAuth: () => request('/auth/exchange', { method: 'POST' }),
  register: (body: any) =>
    request('/users/register', { method: 'POST', body: JSON.stringify(body) }),
  presignUpload: (keyHint: string, contentType: string) =>
    request('/uploads/presign', {
      method: 'POST',
      body: JSON.stringify({ keyHint, contentType }),
    }),
  updateAvatar: ({ key }: { key: string }) =>
    request('/profile/avatar', {
      method: 'POST',
      body: JSON.stringify({ key }),
    }),
  updateDoctorPhoto: ({ key }: { key: string }) =>
    doctorRequest('/work/doctors/photo', {
      method: 'POST',
      body: JSON.stringify({ key }),
    }),

  getAvailableSpecialties: () => request('/specialties/available'),
  listSpecialties: async (): Promise<string[]> => {
    const res = await request('/specialties/available')
    return (Array.isArray(res) ? res : (res?.items ?? [])) as string[]
  },

  createRequest: (body: any) =>
    request('/requests', { method: 'POST', body: JSON.stringify(body) }),
  myRequests: () => request('/requests/mine'),
  deleteRequest: (id: string) => request(`/requests/${id}`, { method: 'DELETE' }),
  openRequestsForSpecialist: () => request('/requests/open'),

  createOffer: (body: any) =>
    request('/offers', { method: 'POST', body: JSON.stringify(body) }),
  myOffersForPatient: () => request('/offers/mine'),
  prepPayment: (offerId: string, chosenSlotISO: string) =>
    request(`/offers/${offerId}/prepare-payment`, {
      method: 'POST',
      body: JSON.stringify({ chosenSlotISO }),
    }),
  finalizeAcceptance: (offerId: string) =>
    request(`/offers/${offerId}/finalise`, { method: 'POST' }),

  stripeOnboard: (returnTo?: string) =>
    request('/stripe/onboard', {
      method: 'POST',
      body: JSON.stringify(returnTo ? { returnTo } : {}),
    }),
  stripeStatus: () => request('/stripe/status'),
  stripeDisconnect: () =>
    request('/stripe/disconnect', {
      method: 'POST',
    }),
  getProviderProfile: (profession: ProviderProfession) => ensureActiveProviderProfile(profession),
  getAppointments: (view?: 'patient' | 'specialist') => {
    const qs = view ? `?view=${view}` : ''
    return request(`/appointments/mine${qs}`)
  },
  getStackAppointments: async (profession: ProviderProfession, view?: 'patient' | 'specialist') => {
    const qs = view ? `?view=${view}` : ''
    // Patients don’t need a provider profile to see their appointments in a stack.
    if (view === 'patient') {
      return stackRequestByProfession(profession)(`/appointments/mine${qs}`)
    }
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) return { appointments: [] }
    return stackRequestByProfession(profession)(`/appointments/mine${qs}`)
  },
  notificationsMine: (role?: 'patient' | 'specialist') => {
    const qs = role ? `?role=${encodeURIComponent(role)}` : ''
    return request(`/notifications/mine${qs}`, {}, { skipAuthSignOut: true })
  },
  notificationsSeen: (ids: string[]) =>
    request('/notifications/seen', { method: 'POST', body: JSON.stringify({ ids }) }, { skipAuthSignOut: true }),
  // If backend supports cancelling legacy appointments, uncomment:
  // cancelAppointment: (offerId: string) =>
  //   request(`/appointments/${offerId}/cancel`, { method: 'POST' }),

  /* -------------------------
     Atlas: Availability
  ------------------------- */
  getMyAvailability: () => doctorRequest('/availability/mine'),
  putMyAvailability: (body: AvailabilityPutBody) =>
    doctorRequest('/availability/mine', { method: 'PUT', body: JSON.stringify(body) }),
  getSlots: (specialistId: string, days: number = 30) =>
    doctorRequest(
      `/availability/slots?specialistId=${encodeURIComponent(specialistId)}&days=${days}`
    ),
  async getMyAvailabilityRange(startISO: string, endISO: string) {
    const qs = `?start=${encodeURIComponent(startISO)}&end=${encodeURIComponent(endISO)}`
    return doctorRequest(`/availability/mine${qs}`)
  },
  async getStackAvailabilityRange(profession: ProviderProfession, startISO: string, endISO: string) {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) return { blocks: [] }
    const qs = `?start=${encodeURIComponent(startISO)}&end=${encodeURIComponent(endISO)}`
    return stackRequestByProfession(profession)(`/availability/mine${qs}`)
  },
  putStackAvailability: async (profession: ProviderProfession, body: AvailabilityPutBody) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) throw new Error('Register as a provider first')
    return stackRequestByProfession(profession)('/availability/mine', {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  },
  googleCalendarStatus: async (profession: ProviderProfession) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) return { connected: false }
    return stackRequestByProfession(profession)('/calendar/google/status')
  },
  googleCalendarStart: async (profession: ProviderProfession, returnTo: string) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) throw new Error('Register as a provider first')
    return stackRequestByProfession(profession)('/calendar/google/start', {
      method: 'POST',
      body: JSON.stringify({ returnTo }),
    })
  },
  googleCalendarCalendars: async (profession: ProviderProfession) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) return { connected: false, calendars: [] }
    return stackRequestByProfession(profession)('/calendar/google/calendars')
  },
  googleCalendarSettings: async (
    profession: ProviderProfession,
    body: { selectedCalendarIds?: string[]; writeCalendarId?: string | null; autoCreateMeetings?: boolean }
  ) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) throw new Error('Register as a provider first')
    return stackRequestByProfession(profession)('/calendar/google/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  },
  googleCalendarBusy: async (profession: ProviderProfession, startISO: string, endISO: string) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) return { busy: [] }
    const qs = `?start=${encodeURIComponent(startISO)}&end=${encodeURIComponent(endISO)}`
    return stackRequestByProfession(profession)(`/calendar/google/busy${qs}`)
  },
  googleCalendarDisconnect: async (profession: ProviderProfession) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) return { ok: true }
    return stackRequestByProfession(profession)('/calendar/google/disconnect', {
      method: 'POST',
      body: JSON.stringify({}),
    })
  },
  calendarSettings: async (profession: ProviderProfession) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) return { timeZone: null }
    return stackRequestByProfession(profession)('/calendar/settings')
  },
  calendarSettingsUpdate: async (
    profession: ProviderProfession,
    body: { timeZone?: string | null }
  ) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) throw new Error('Register as a provider first')
    return stackRequestByProfession(profession)('/calendar/settings', {
      method: 'PUT',
      body: JSON.stringify(body || {}),
    })
  },
  calendlyStatus: async (profession: ProviderProfession) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) return { connected: false }
    return stackRequestByProfession(profession)('/calendar/calendly/status')
  },
  calendlyStart: async (profession: ProviderProfession, returnTo: string) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) throw new Error('Register as a provider first')
    return stackRequestByProfession(profession)('/calendar/calendly/start', {
      method: 'POST',
      body: JSON.stringify({ returnTo }),
    })
  },
  calendlyEventTypes: async (profession: ProviderProfession) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) return { connected: false, eventTypes: [] }
    return stackRequestByProfession(profession)('/calendar/calendly/event-types')
  },
  calendlySettings: async (
    profession: ProviderProfession,
    body: { selectedEventTypeUris: string[] }
  ) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) throw new Error('Register as a provider first')
    return stackRequestByProfession(profession)('/calendar/calendly/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  },
  calendlyBusy: async (profession: ProviderProfession, startISO: string, endISO: string) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) return { busy: [] }
    const qs = `?start=${encodeURIComponent(startISO)}&end=${encodeURIComponent(endISO)}`
    return stackRequestByProfession(profession)(`/calendar/calendly/busy${qs}`)
  },
  calendlyDisconnect: async (profession: ProviderProfession) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) return { ok: true }
    return stackRequestByProfession(profession)('/calendar/calendly/disconnect', {
      method: 'POST',
      body: JSON.stringify({}),
    })
  },
  zoomStatus: async (profession: ProviderProfession) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) return { connected: false }
    return stackRequestByProfession(profession)('/calendar/zoom/status')
  },
  zoomStart: async (profession: ProviderProfession, returnTo: string) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) throw new Error('Register as a provider first')
    return stackRequestByProfession(profession)('/calendar/zoom/start', {
      method: 'POST',
      body: JSON.stringify({ returnTo }),
    })
  },
  zoomSettings: async (profession: ProviderProfession, body: { autoCreateMeetings?: boolean }) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) throw new Error('Register as a provider first')
    return stackRequestByProfession(profession)('/calendar/zoom/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  },
  zoomDisconnect: async (profession: ProviderProfession) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) return { ok: true }
    return stackRequestByProfession(profession)('/calendar/zoom/disconnect', {
      method: 'POST',
      body: JSON.stringify({}),
    })
  },

  submitSpecialistReview: (
    profession: ProviderProfession,
    payload: { selectionId: string; specialistId: string; rating: number; comment?: string },
  ) =>
    stackRequestByProfession(profession)('/reviews/providers', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  submitClientReview: (
    profession: ProviderProfession,
    payload: { selectionId: string; patientId: string; rating: number },
  ) =>
    stackRequestByProfession(profession)('/reviews/clients', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  replyToSpecialistReview: (
    profession: ProviderProfession,
    payload: { selectionId: string; patientId: string; comment: string },
  ) =>
    stackRequestByProfession(profession)('/reviews/providers/reply', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  getSpecialistReviews: (
    specialistId: string,
    profession: ProviderProfession = 'doctor',
    opts?: { cursor?: string | null; limit?: number },
  ) => {
    const qs = new URLSearchParams()
    if (opts?.cursor) qs.set('cursor', opts.cursor)
    if (opts?.limit) qs.set('limit', String(opts.limit))
    const suffix = qs.toString()
    return stackRequestByProfession(profession)(
      `/reviews/providers/${encodeURIComponent(specialistId)}${suffix ? `?${suffix}` : ''}`
    )
  },
  getClientRatingSummary: async (patientId: string, profession: ProviderProfession = 'doctor') => {
    try {
      return await stackRequestByProfession(profession)(`/reviews/clients/${encodeURIComponent(patientId)}`)
    } catch (err: any) {
      const msg = String(err?.message || '').toLowerCase()
      if (msg.includes('unknown role') || msg.includes('not found')) {
        return null
      }
      throw err
    }
  },

  /* -------------------------
     Atlas: Selections (Patient)
  ------------------------- */
  createSelection: (body: CreateSelectionBody & { profession: ProviderProfession }) => {
    const { profession, ...rest } = body
    return stackRequestByProfession(profession)('/selections', {
      method: 'POST',
      body: JSON.stringify(rest),
    })
  },

  createGuestSelection: (body: GuestSelectionBody & { profession: ProviderProfession }) => {
    const { profession, ...rest } = body
    return stackRequestByProfession(profession)('/public/requests/guest', {
      method: 'POST',
      body: JSON.stringify(rest),
    })
  },

  attachSelectionPayment: (
    profession: ProviderProfession,
    selectionId: string,
    paymentMethodId?: string,
  ) =>
    stackRequestByProfession(profession)(`/selections/${selectionId}/attach-payment`, {
      method: 'POST',
      body: JSON.stringify(paymentMethodId ? { paymentMethodId } : {}),
    }),

  fundSelection: (
    profession: ProviderProfession,
    selectionId: string,
    payload: { specialistId: string; paymentMethodId?: string; amountCents?: number },
  ) =>
    stackRequestByProfession(profession)(`/selections/${selectionId}/fund`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    }),

  completeSelection: (
    profession: ProviderProfession,
    selectionId: string,
  ) =>
    stackRequestByProfession(profession)(`/selections/${selectionId}/complete`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  confirmSelectionComplete: (
    profession: ProviderProfession,
    selectionId: string,
  ) =>
    stackRequestByProfession(profession)(`/selections/${selectionId}/confirm-complete`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  mySelections: (): Promise<{ items: SelectionSummary[] }> =>
    doctorRequest('/selections/mine'),
  selectionsMine: (profession: ProviderProfession = 'doctor') =>
    stackRequestByProfession(profession)('/selections/mine'),

  getSelection: (selectionId: string) =>
    doctorRequest(`/selections/${selectionId}`).catch(() => getSelectionFromMine(selectionId)),

  // Optional (if supported server-side)
  cancelSelection: (
    professionOrSelectionId: ProviderProfession | string,
    selectionId?: string
  ) => {
    if (selectionId) {
      return stackRequestByProfession(professionOrSelectionId as ProviderProfession)(
        `/selections/${selectionId}/cancel`,
        { method: 'POST' }
      )
    }
    return doctorRequest(`/selections/${professionOrSelectionId}/cancel`, { method: 'POST' })
  },

  /* -------------------------
     Atlas: Inbox (Specialist)
  ------------------------- */
  incomingSelections: async (profession: ProviderProfession = 'doctor') => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) return { items: [] }
    try {
      return await stackRequestByProfession(profession)('/selections/incoming')
    } catch (err: any) {
      const msg = String(err?.message || '').toLowerCase()
      if (msg.includes('bad request') || msg.includes('no specialist') || msg.includes('missing provider')) {
        return { items: [] }
      }
      throw err
    }
  },
  acceptSelection: (
    profession: ProviderProfession,
    selectionId: string,
    feeCents?: number,
    paymentMethodId?: string,
  ) => {
    const body: Record<string, any> = {}
    if (paymentMethodId) body.paymentMethodId = paymentMethodId
    if (typeof feeCents === 'number' && !Number.isNaN(feeCents)) body.feeCents = feeCents
    return stackRequestByProfession(profession)(`/selections/${selectionId}/accept`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },
  declineSelection: (profession: ProviderProfession, selectionId: string) =>
    stackRequestByProfession(profession)(`/selections/${selectionId}/decline`, { method: 'POST' }),
}

/* -------------------------
   Batch slots (optional perf helper)
------------------------- */
export const getBatchSlots = (ids: string[], days = 30, limit = 6) =>
  doctorRequest(
    `/availability/slots:batch?ids=${encodeURIComponent(ids.join(','))}&days=${days}&limit=${limit}`
  )

/* -------------------------
   Atlas convenience (namespaced)
------------------------- */
export const atlas = {
  // 🔹 search specialists (legacy helper)
  searchSpecialists: (
    profession: ProviderProfession = 'doctor',
    body: {
      specialty: string
      reason?: string
      mode?: 'IN_PERSON'|'TELEHEALTH'|'EITHER'
      location?: { state?: string }
      limit?: number
      sort?: 'recommended' | 'rating' | 'availability' | 'price'
    },
  ) => stackRequestByProfession(profession)('/specialists/search', {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  availabilitySlots: (profession: ProviderProfession = 'doctor', specialistId: string, days = 30) =>
    stackRequestByProfession(profession)(
      `/availability/slots?specialistId=${encodeURIComponent(specialistId)}&days=${days}`
    ),

  selectionsCreate: (
    profession: ProviderProfession,
    body: CreateSelectionBody,
  ) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)('/selections', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  createManualBooking: (
    profession: ProviderProfession,
    body: {
      firstName: string
      lastName: string
      email: string
      phone: string
      address?: string
      slotISO?: string
    },
  ) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)('/selections/manual', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  selectionsMine: (profession: ProviderProfession = 'doctor') => {
    if (!isStackEnabled(profession)) return Promise.resolve({ items: [] })
    return stackRequestByProfession(profession)('/selections/mine')
  },

  getSelection: (profession: ProviderProfession = 'doctor', selectionId: string) => {
    if (!isStackEnabled(profession)) return Promise.resolve(null)
    return stackRequestByProfession(profession)(`/selections/${selectionId}`)
  },

  cancelSelection: (profession: ProviderProfession, selectionId: string) =>
    stackRequestByProfession(profession)(`/selections/${selectionId}/cancel`, { method: 'POST' }),

  getSelectionNotes: (profession: ProviderProfession = 'doctor', selectionId: string) => {
    if (!isStackEnabled(profession)) {
      return Promise.resolve({ notes: '', updatedAt: null, publishedAt: null, attachments: [] })
    }
    return stackRequestByProfession(profession)(`/selections/${selectionId}/notes`)
  },

  saveSelectionNotes: (
    profession: ProviderProfession = 'doctor',
    selectionId: string,
    notes: string,
    published?: boolean,
    attachments?: Array<{
      key: string
      name?: string
      type?: string
      size?: number
      kind?: 'image' | 'file'
      uploadedAt?: string
    }>,
  ) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    const payload: Record<string, any> = { notes }
    if (typeof published === 'boolean') payload.published = published
    if (attachments) payload.attachments = attachments
    return stackRequestByProfession(profession)(`/selections/${selectionId}/notes`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  },

  generateSelectionNotesSummary: (
    profession: ProviderProfession = 'doctor',
    selectionId: string,
  ) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)(`/selections/${selectionId}/notes/summary`, {
      method: 'POST',
    })
  },
  generateSelectionNotesDraft: (
    profession: ProviderProfession = 'doctor',
    selectionId: string,
    prompt: string,
  ) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)(`/selections/${selectionId}/notes/draft`, {
      method: 'POST',
      body: JSON.stringify({ prompt }),
    })
  },

  saveSelectionNotesSummary: (
    profession: ProviderProfession = 'doctor',
    selectionId: string,
    summary: string,
  ) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)(`/selections/${selectionId}/notes/summary`, {
      method: 'PUT',
      body: JSON.stringify({ summary }),
    })
  },

  sendReviewReminder: (
    profession: ProviderProfession = 'doctor',
    selectionId: string,
    payload: { message?: string; saveDefault?: boolean } = {},
  ) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)('/reviews/providers/remind', {
      method: 'POST',
      body: JSON.stringify({ selectionId, ...payload }),
    })
  },

  listSelectionInvoices: (profession: ProviderProfession = 'doctor', selectionId: string) => {
    if (!isStackEnabled(profession)) return Promise.resolve({ invoices: [] })
    return stackRequestByProfession(profession)(`/selections/${selectionId}/invoices`)
  },

  createSelectionInvoice: (
    profession: ProviderProfession = 'doctor',
    selectionId: string,
    payload: {
      amountCents: number
      description?: string
      memo?: string
      daysUntilDue?: number
      includeGst?: boolean
      abn?: string
      saveAbn?: boolean
      passStripeFee?: boolean
    },
  ) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)(`/selections/${selectionId}/invoices`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  resendInvoice: (profession: ProviderProfession = 'doctor', invoiceId: string) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)(`/invoices/${invoiceId}/resend`, { method: 'POST' })
  },

  voidInvoice: (profession: ProviderProfession = 'doctor', invoiceId: string) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)(`/invoices/${invoiceId}/void`, { method: 'POST' })
  },

  createSelectionMeeting: (
    profession: ProviderProfession = 'doctor',
    selectionId: string,
    meetingTimeISO?: string,
    meetingTimeZone?: string,
    meetingProvider?: 'zoom' | 'google',
  ) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    const payload: Record<string, any> = {}
    if (meetingTimeISO) payload.meetingTimeISO = meetingTimeISO
    if (meetingTimeZone) payload.meetingTimeZone = meetingTimeZone
    if (meetingProvider) payload.meetingProvider = meetingProvider
    const body = Object.keys(payload).length ? JSON.stringify(payload) : undefined
    return stackRequestByProfession(profession)(`/selections/${selectionId}/meeting`, {
      method: 'POST',
      body,
    })
  },

  resendSelectionMeeting: (profession: ProviderProfession = 'doctor', selectionId: string) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)(`/selections/${selectionId}/meeting/resend`, { method: 'POST' })
  },

  presignUpload: (
    profession: ProviderProfession = 'doctor',
    keyHint: string,
    contentType: string,
  ) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)('/uploads/presign', {
      method: 'POST',
      body: JSON.stringify({ keyHint, contentType }),
    })
  },

  signUploadKey: (profession: ProviderProfession = 'doctor', key: string) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    const qs = `?key=${encodeURIComponent(key)}`
    return stackRequestByProfession(profession)(`/uploads/sign${qs}`)
  },

  chatOpenConversation: (
    profession: ProviderProfession,
    payload: {
      selectionId?: string
      patientId?: string
      specialistId?: string
      peerName?: string
      peerEmail?: string
      peerPhone?: string
      patientName?: string
    },
  ): Promise<{ conversation: ChatConversation }> => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)('/chat/conversations/open', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    })
  },

  chatRealtimeConfig: (
    profession: ProviderProfession,
  ): Promise<{ wsUrl?: string | null; stack?: string | null }> => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)('/chat/realtime/config')
  },

  chatListConversations: (
    profession: ProviderProfession,
    options: { limit?: number; cursor?: string | null } = {},
  ): Promise<{ items: ChatConversation[]; nextCursor?: string | null }> => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    const params = new URLSearchParams()
    if (options.limit) params.set('limit', String(options.limit))
    if (options.cursor) params.set('cursor', options.cursor)
    const qs = params.toString() ? `?${params.toString()}` : ''
    return stackRequestByProfession(profession)(`/chat/conversations/mine${qs}`)
  },

  chatGetMessages: (
    profession: ProviderProfession,
    conversationId: string,
    options: { limit?: number; cursor?: string | null } = {},
  ): Promise<{ items: ChatMessage[]; nextCursor?: string | null }> => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    const params = new URLSearchParams()
    if (options.limit) params.set('limit', String(options.limit))
    if (options.cursor) params.set('cursor', options.cursor)
    const qs = params.toString() ? `?${params.toString()}` : ''
    return stackRequestByProfession(profession)(
      `/chat/conversations/${encodeURIComponent(conversationId)}/messages${qs}`
    )
  },

  chatSendMessage: (
    profession: ProviderProfession,
    conversationId: string,
    payload: {
      text?: string
      attachments?: ChatAttachment[]
      booking?: {
        slotISO?: string
        status?: string
        note?: string
        selectionId?: string
      } | null
      selectionId?: string | null
    },
  ): Promise<{ message: ChatMessage; emailSent?: boolean; conversation?: ChatConversation }> => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)(
      `/chat/conversations/${encodeURIComponent(conversationId)}/messages`,
      {
        method: 'POST',
        body: JSON.stringify(payload || {}),
      }
    )
  },

  chatMarkRead: (
    profession: ProviderProfession,
    conversationId: string,
  ): Promise<{ ok: boolean; updated: number; readAt: string }> => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)(
      `/chat/conversations/${encodeURIComponent(conversationId)}/read`,
      { method: 'POST', body: JSON.stringify({}) }
    )
  },

  chatSignAttachment: (
    profession: ProviderProfession,
    conversationId: string,
    key: string,
  ): Promise<{ key: string; url: string }> => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    const qs = `?key=${encodeURIComponent(key)}`
    return stackRequestByProfession(profession)(
      `/chat/conversations/${encodeURIComponent(conversationId)}/attachments/sign${qs}`
    )
  },

  selectionsAttachPayment: (
    profession: ProviderProfession,
    selectionId: string,
    paymentMethodId?: string,
  ) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    const body = paymentMethodId ? { paymentMethodId } : {}
    return stackRequestByProfession(profession)(`/selections/${selectionId}/attach-payment`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  fundSelection: (
    profession: ProviderProfession,
    selectionId: string,
    payload: { specialistId: string; paymentMethodId?: string; amountCents?: number },
  ) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)(`/selections/${selectionId}/fund`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    })
  },

  completeSelection: (
    profession: ProviderProfession,
    selectionId: string,
  ) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)(`/selections/${selectionId}/complete`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  },

  confirmSelectionComplete: (
    profession: ProviderProfession,
    selectionId: string,
  ) => {
    if (!isStackEnabled(profession)) return Promise.reject(new Error('Stack disabled'))
    return stackRequestByProfession(profession)(`/selections/${selectionId}/confirm-complete`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  },

  getIncomingSelections: async (
    profession: ProviderProfession = 'doctor',
    options: { includeAll?: boolean; cursor?: string } = {},
  ) => {
    const profile = await ensureActiveProviderProfile(profession)
    if (!profile) return { items: [] }
    try {
      const params = new URLSearchParams()
      if (options.includeAll) params.set('includeAll', 'true')
      if (options.cursor) params.set('cursor', options.cursor)
      const qs = params.toString() ? `?${params.toString()}` : ''
      return await stackRequestByProfession(profession)(`/selections/incoming${qs}`)
    } catch (err: any) {
      const msg = String(err?.message || '').toLowerCase()
      if (msg.includes('bad request') || msg.includes('no specialist') || msg.includes('missing provider')) {
        return { items: [] }
      }
      throw err
    }
  },

  acceptCandidate: (
    profession: ProviderProfession,
    selectionId: string,
    feeCents: number,
    paymentMethodId: string,
  ) =>
    stackRequestByProfession(profession)(`/selections/${selectionId}/accept`, {
      method: 'POST',
      body: JSON.stringify({ feeCents, paymentMethodId }),
    }),

  declineCandidate: (profession: ProviderProfession, selectionId: string) =>
    stackRequestByProfession(profession)(`/selections/${selectionId}/decline`, { method: 'POST' }),

  rescheduleSelection: (
    profession: ProviderProfession,
    selectionId: string,
    payload: { slotISO: string; paymentMethodId?: string }
  ) =>
    stackRequestByProfession(profession)(`/selections/${selectionId}/reschedule`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  rebookSelection: (
    profession: ProviderProfession,
    selectionId: string,
    payload: { slotISO: string; reason: string; paymentMethodId: string }
  ) =>
    stackRequestByProfession(profession)(`/selections/${selectionId}/rebook`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
}

/* -------------------------
   Registration helpers
------------------------- */
export function registerClientProfile(body: {
  firstName: string
  lastName: string
  phone: string
  email: string
  timeZone?: string
  turnstileToken: string
}) {
  const payload = {
    firstName: body.firstName,
    lastName: body.lastName,
    name: `${body.firstName} ${body.lastName}`.replace(/\s+/g, ' ').trim(),
    phone: body.phone,
    email: body.email,
    timeZone: body.timeZone || null,
    turnstileToken: body.turnstileToken,
  }
  return request('/register/client', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// src/api.ts (add near your existing exports)
const PROVIDER_STACKS: Record<ProviderProfession, {
  request: typeof doctorRequest,
  specialtiesPath: string,
  searchPath: string,
  availabilityPath: (id: string, days: number) => string,
}> = {
  doctor: {
    request: doctorRequest,
    specialtiesPath: '/specialties/available',
    searchPath: '/specialists/search',
    availabilityPath: (id, days) => `/availability/slots?specialistId=${encodeURIComponent(id)}&days=${days}`,
  },
  education: {
    request: educationRequest,
    specialtiesPath: '/specialties/available',
    searchPath: '/specialists/search',
    availabilityPath: (id, days) => `/availability/slots?specialistId=${encodeURIComponent(id)}&days=${days}`,
  },
  tech: {
    request: techRequest,
    specialtiesPath: '/specialties/available',
    searchPath: '/specialists/search',
    availabilityPath: (id, days) => `/availability/slots?specialistId=${encodeURIComponent(id)}&days=${days}`,
  },
  property: {
    request: propertyRequest,
    specialtiesPath: '/specialties/available',
    searchPath: '/specialists/search',
    availabilityPath: (id, days) => `/availability/slots?specialistId=${encodeURIComponent(id)}&days=${days}`,
  },
  financial: {
    request: financialRequest,
    specialtiesPath: '/specialties/available',
    searchPath: '/specialists/search',
    availabilityPath: (id, days) => `/availability/slots?specialistId=${encodeURIComponent(id)}&days=${days}`,
  },
  allied: {
    request: alliedRequest,
    specialtiesPath: '/specialties/available',
    searchPath: '/specialists/search',
    availabilityPath: (id, days) => `/availability/slots?specialistId=${encodeURIComponent(id)}&days=${days}`,
  },
  migration: {
    request: migrationRequest,
    specialtiesPath: '/specialties/available',
    searchPath: '/specialists/search',
    availabilityPath: (id, days) => `/availability/slots?specialistId=${encodeURIComponent(id)}&days=${days}`,
  },
  legal: {
    request: legalRequest,
    specialtiesPath: '/specialties/available',
    searchPath: '/specialists/search',
    availabilityPath: (id, days) => `/availability/slots?specialistId=${encodeURIComponent(id)}&days=${days}`,
  },
}

const getStackConfig = (profession: ProviderProfession) => {
  return PROVIDER_STACKS[profession] || PROVIDER_STACKS.doctor
}

export const professionsApi = {
  async listSpecialties(profession: ProviderProfession): Promise<string[]> {
    const stack = getStackConfig(profession)
    const res = await stack.request(stack.specialtiesPath)
    if (Array.isArray(res)) return res as string[]
    return (res?.items ?? []) as string[]
  },
}

export const atlasGeneric = {
  async searchProfessionals(params: {
    profession: ProviderProfession,
    specialty: string,
    reason?: string,
    state?: string,
    sort?: 'recommended' | 'rating' | 'availability' | 'price',
    limit?: number,
  }) {
    const { profession, specialty, reason, state, sort, limit = 30 } = params
    const stack = getStackConfig(profession)
    return stack.request(stack.searchPath, {
      method: 'POST',
      body: JSON.stringify({
        specialty,
        reason,
        mode: 'EITHER',
        location: state ? { state } : undefined,
        limit,
        sort,
      }),
    })
  },

  async availabilitySlots(profession: ProviderProfession, id: string, days: number) {
    const stack = getStackConfig(profession)
    return stack.request(stack.availabilityPath(id, days))
  },
}

export async function doctorMe() {
  const res = await doctorRequest('/work/doctors/me')
  const normalized = normalizeProviderProfileResponse(res)
  if (normalized) cacheProviderProfile('doctor', normalized)
  return res
}

export async function educationMe() {
  const res = await educationRequest('/work/education/me')
  const normalized = normalizeProviderProfileResponse(res)
  if (normalized) cacheProviderProfile('education', normalized)
  return res
}

export async function techMe() {
  const res = await techRequest('/work/tech/me')
  const normalized = normalizeProviderProfileResponse(res)
  if (normalized) cacheProviderProfile('tech', normalized)
  return res
}

export async function propertyMe() {
  const res = await propertyRequest('/work/property/me')
  const normalized = normalizeProviderProfileResponse(res)
  if (normalized) cacheProviderProfile('property', normalized)
  return res
}

export async function financialMe() {
  const res = await financialRequest('/work/financial/me')
  const normalized = normalizeProviderProfileResponse(res)
  if (normalized) cacheProviderProfile('financial', normalized)
  return res
}

export async function alliedMe() {
  const res = await alliedRequest('/work/allied/me')
  const normalized = normalizeProviderProfileResponse(res)
  if (normalized) cacheProviderProfile('allied', normalized)
  return res
}

export async function legalMe() {
  const res = await legalRequest('/work/legal/me')
  const normalized = normalizeProviderProfileResponse(res)
  if (normalized) cacheProviderProfile('legal', normalized)
  return res
}

export async function migrationMe() {
  const res = await migrationRequest('/work/migration/me')
  const normalized = normalizeProviderProfileResponse(res)
  if (normalized) cacheProviderProfile('migration', normalized)
  return res
}

function normalizeProviderProfileResponse(res: any) {
  if (!res) return null
  const base =
    res && typeof res === 'object' && !Array.isArray(res) && res.profile && typeof res.profile === 'object'
      ? { ...res.profile }
      : res && typeof res === 'object' && !Array.isArray(res)
      ? { ...res }
      : null
  if (!base) return null
  const status = res?.status ?? base.status
  if (status && !base.status) base.status = status
  if (res?.approvalStatus && !base.approvalStatus) base.approvalStatus = res.approvalStatus
  return base
}

function providerProfileHasDetails(profession: ProviderProfession, profile: any) {
  if (!profile || typeof profile !== 'object') return false
  const specialties = Array.isArray(profile.specialties) ? profile.specialties.filter(Boolean) : []
  if (specialties.length) return true
  if (profession === 'doctor') {
    return Boolean(
      profile.ahpraNumber ||
        profile.documents ||
        profile.pricing ||
        profile.practiceName ||
        profile.profile
    )
  }
  if (profession === 'education') {
    return Boolean(
      profile.teacherRegistrationNumber ||
        profile.documents ||
        profile.pricing ||
        profile.practiceName ||
        profile.profile
    )
  }
  if (profession === 'allied') {
    return Boolean(
      profile.registrationNumber ||
        profile.providerRegistrationNumber ||
        profile.documents ||
        profile.pricing ||
        profile.practiceName ||
        profile.profile
    )
  }
  if (profession === 'legal') {
    return Boolean(
      profile.registrationNumber ||
        profile.providerRegistrationNumber ||
        profile.practiceRegistrationNumber ||
        profile.documents ||
        profile.pricing ||
        profile.practiceName ||
        profile.profile
    )
  }
  // property, tech, financial, migration and any similar stacks share shape
  if (profession === 'tech' || profession === 'property' || profession === 'financial' || profession === 'migration') {
    return Boolean(
      profile.registrationNumber ||
        profile.providerRegistrationNumber ||
        profile.agentRegistrationNumber ||
        profile.documents ||
        profile.pricing ||
        profile.practiceName ||
        profile.profile
    )
  }
  return false
}

export async function fetchProviderAccountProfile(profession: ProviderProfession) {
  try {
    let raw: any = null
    switch (profession) {
      case 'education':
        raw = await educationMe()
        break
      case 'tech':
        raw = await techMe()
        break
      case 'property':
        raw = await propertyMe()
        break
      case 'financial':
        raw = await financialMe()
        break
      case 'allied':
        raw = await alliedMe()
        break
      case 'legal':
        raw = await legalMe()
        break
      case 'migration':
        raw = await migrationMe()
        break
      default:
        raw = await doctorMe()
    }
    const normalized = raw && typeof raw === 'object' && normalizeProviderProfileResponse(raw)
    if (normalized && providerProfileHasDetails(profession, normalized)) return normalized
  } catch {
  }
  return null
}

export const enabledStacks = enabledStackList
export const isStackEnabled = (profession: ProviderProfession) => enabledStackSet.has(profession)

export async function adminGetProvider(params: { userId?: string; email?: string }) {
  const qp = new URLSearchParams()
  if (params.userId) qp.set('userId', params.userId)
  if (params.email) qp.set('email', params.email)
  const qs = qp.toString()
  return await request(`/admin/providers${qs ? `?${qs}` : ''}`, { method: 'GET' })
}

export async function adminListUsers(params: {
  role?: 'all' | 'clients' | 'providers'
  status?: 'approved' | 'pending'
  sort?: 'created' | 'userId'
  contains?: string
}) {
  const qp = new URLSearchParams()
  if (params.role) qp.set('role', params.role)
  if (params.status) qp.set('status', params.status)
  if (params.sort) qp.set('sort', params.sort)
  if (params.contains) qp.set('contains', params.contains)
  const qs = qp.toString()
  return await request(`/admin/users/list${qs ? `?${qs}` : ''}`, { method: 'GET' })
}

export async function adminListRequestsByProfession(
  profession: ProviderProfession,
  params: { limit?: number; cursor?: string } = {}
) {
  if (!isStackEnabled(profession)) return { ok: true, items: [], nextCursor: null }
  const qp = new URLSearchParams()
  if (params.limit) qp.set('limit', String(params.limit))
  if (params.cursor) qp.set('cursor', params.cursor)
  const qs = qp.toString()
  return await stackRequestByProfession(profession)(`/admin/requests${qs ? `?${qs}` : ''}`, { method: 'GET' })
}

export async function adminListProviderStats(
  profession: ProviderProfession,
  params: { limit?: number; cursor?: string } = {}
) {
  if (!isStackEnabled(profession)) return { ok: true, items: [], nextCursor: null }
  const qp = new URLSearchParams()
  if (params.limit) qp.set('limit', String(params.limit))
  if (params.cursor) qp.set('cursor', params.cursor)
  const qs = qp.toString()
  return await stackRequestByProfession(profession)(`/admin/stats${qs ? `?${qs}` : ''}`, { method: 'GET' })
}

export async function adminUpdateProvider(payload: { userId: string; updates: Record<string, any>; note?: string }) {
  if (!payload?.userId) throw new Error('userId is required')
  return await request('/admin/providers/update', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function adminPresignProviderUpload(payload: { userId: string; keyHint?: string; contentType?: string }) {
  if (!payload?.userId) throw new Error('userId is required')
  return await request('/admin/uploads/presign', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function adminGetProviderByProfession(
  profession: ProviderProfession,
  params: { userId?: string; email?: string }
) {
  const userId = params.userId?.trim()
  const email = params.email?.trim()
  if (!userId && !email) throw new Error('userId or email is required')
  // Prefer direct userId lookups when available to avoid expensive email scans.
  if (userId) {
    return await stackRequestByProfession(profession)(`/admin/providers/${encodeURIComponent(userId)}`, {
      method: 'GET',
    })
  }
  if (email) {
    const qp = new URLSearchParams()
    qp.set('email', email)
    return await stackRequestByProfession(profession)(`/admin/providers?${qp.toString()}`, { method: 'GET' })
  }
  throw new Error('userId or email is required')
}

export async function adminApproveProvider(profession: ProviderProfession, userId: string) {
  if (!userId) throw new Error('userId is required')
  return await stackRequestByProfession(profession)('/admin/approve', {
    method: 'POST',
    body: JSON.stringify({ userId }),
  })
}

export async function adminUnapproveProvider(profession: ProviderProfession, userId: string) {
  if (!userId) throw new Error('userId is required')
  return await stackRequestByProfession(profession)('/admin/unapprove', {
    method: 'POST',
    body: JSON.stringify({ userId }),
  })
}

export async function adminSetProviderDisplay(payload: {
  userId: string
  stackId: ProviderProfession
  label?: string
  clear?: boolean
}) {
  if (!payload?.userId) throw new Error('userId is required')
  if (!payload?.stackId) throw new Error('stackId is required')
  return await request('/admin/providers/display', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function adminProvisionProvider(payload: {
  email: string
  firstName: string
  lastName: string
  stackId: ProviderProfession
  specialties?: string[]
}) {
  if (!payload?.email) throw new Error('email is required')
  if (!payload?.firstName || !payload?.lastName) throw new Error('firstName and lastName are required')
  if (!payload?.stackId) throw new Error('stackId is required')
  return await request('/admin/providers/provision', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export type AdminEmailTemplateItem = {
  template: string
  title: string
  description?: string
  variables: string[]
  subjectTemplate: string
  htmlTemplate: string
  defaultSubject: string
  defaultHtml: string
  isCustomized?: boolean
  isDeleted?: boolean
  unknownVariables?: string[]
  versions?: AdminEmailTemplateVersionItem[]
  updatedAt?: string | null
  updatedBy?: string | null
}

export type AdminEmailTemplateVersionItem = {
  versionId: string
  updatedAt?: string | null
  updatedBy?: string | null
  deleted?: boolean
  note?: string | null
}

export async function adminListEmailTemplates(
  options: { includeDeleted?: boolean } = {}
): Promise<{ items: AdminEmailTemplateItem[] }> {
  const sp = new URLSearchParams()
  if (options.includeDeleted) sp.set('includeDeleted', 'true')
  const qs = sp.toString()
  return await request(`/admin/providers/emails${qs ? `?${qs}` : ''}`, { method: 'GET' })
}

export async function adminSaveEmailTemplate(payload: {
  template: string
  subjectTemplate?: string
  htmlTemplate?: string
  delete?: boolean
  rollbackVersionId?: string
  reset?: boolean
}): Promise<{
  ok: boolean
  template: string
  deleted?: boolean
  rolledBack?: boolean
  reset?: boolean
  item?: AdminEmailTemplateItem | null
  items?: AdminEmailTemplateItem[]
}> {
  return await request('/admin/providers/emails', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function adminPreviewEmailTemplate(payload: {
  template: string
  subjectTemplate?: string
  htmlTemplate?: string
  previewData?: Record<string, any>
}): Promise<{
  ok: boolean
  template: string
  subjectPreview: string
  htmlPreview: string
  htmlPreviewWrapped?: string
  previewData?: Record<string, any>
  unknownVariables?: string[]
  missingVariables?: string[]
  allowedVariables?: string[]
  usedVariables?: string[]
}> {
  return await request('/admin/providers/emails/preview', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function adminListStackEmailTemplates(
  stack: ProviderProfession,
  options: { includeDeleted?: boolean } = {}
): Promise<{ items: AdminEmailTemplateItem[] }> {
  const stackReq = stackRequestByProfession(stack)
  const sp = new URLSearchParams()
  if (options.includeDeleted) sp.set('includeDeleted', 'true')
  const qs = sp.toString()
  return await stackReq(`/admin/providers/emails${qs ? `?${qs}` : ''}`, { method: 'GET' })
}

export async function adminPreviewStackEmailTemplate(
  stack: ProviderProfession,
  payload: {
    template: string
    subjectTemplate?: string
    htmlTemplate?: string
    previewData?: Record<string, any>
  }
): Promise<{
  ok: boolean
  template: string
  subjectPreview: string
  htmlPreview: string
  htmlPreviewWrapped?: string
  previewData?: Record<string, any>
  unknownVariables?: string[]
  missingVariables?: string[]
  allowedVariables?: string[]
  usedVariables?: string[]
}> {
  const stackReq = stackRequestByProfession(stack)
  return await stackReq('/admin/providers/emails/preview', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function adminSaveStackEmailTemplate(
  stack: ProviderProfession,
  payload: {
    template: string
    subjectTemplate?: string
    htmlTemplate?: string
    delete?: boolean
    rollbackVersionId?: string
    reset?: boolean
  }
): Promise<{
  ok: boolean
  template: string
  deleted?: boolean
  rolledBack?: boolean
  reset?: boolean
  item?: AdminEmailTemplateItem | null
  items?: AdminEmailTemplateItem[]
}> {
  const stackReq = stackRequestByProfession(stack)
  return await stackReq('/admin/providers/emails', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// Doctor admin endpoints (stack-level)
export async function adminDoctorGetProvider(userId: string) {
  if (!userId) throw new Error('userId is required')
  return await doctorRequest(`/admin/providers/${encodeURIComponent(userId)}`, { method: 'GET' })
}

export async function adminDoctorUpdateProvider(userId: string, payload: Record<string, any>) {
  if (!userId) throw new Error('userId is required')
  return await doctorRequest(`/admin/providers/${encodeURIComponent(userId)}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function adminDoctorPresignUpload(payload: { userId: string; keyHint?: string; contentType?: string }) {
  if (!payload?.userId) throw new Error('userId is required')
  return await doctorRequest('/admin/uploads/presign', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// Education admin endpoints (stack-level)
export async function adminEducationGetProvider(userId: string) {
  if (!userId) throw new Error('userId is required')
  return await educationRequest(`/admin/providers/${encodeURIComponent(userId)}`, { method: 'GET' })
}

export async function adminEducationUpdateProvider(userId: string, payload: Record<string, any>) {
  if (!userId) throw new Error('userId is required')
  return await educationRequest(`/admin/providers/${encodeURIComponent(userId)}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function adminEducationPresignUpload(payload: { userId: string; keyHint?: string; contentType?: string }) {
  if (!payload?.userId) throw new Error('userId is required')
  return await educationRequest('/admin/uploads/presign', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// Migration admin endpoints (stack-level)
export async function adminMigrationGetProvider(userId: string) {
  if (!userId) throw new Error('userId is required')
  return await migrationRequest(`/admin/providers/${encodeURIComponent(userId)}`, { method: 'GET' })
}

export async function adminMigrationUpdateProvider(userId: string, payload: Record<string, any>) {
  if (!userId) throw new Error('userId is required')
  return await migrationRequest(`/admin/providers/${encodeURIComponent(userId)}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function adminMigrationPresignUpload(payload: { userId: string; keyHint?: string; contentType?: string }) {
  if (!payload?.userId) throw new Error('userId is required')
  return await migrationRequest('/admin/uploads/presign', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// Tech admin endpoints (stack-level)
export async function adminTechGetProvider(userId: string) {
  if (!userId) throw new Error('userId is required')
  return await techRequest(`/admin/providers/${encodeURIComponent(userId)}`, { method: 'GET' })
}

export async function adminTechUpdateProvider(userId: string, payload: Record<string, any>) {
  if (!userId) throw new Error('userId is required')
  return await techRequest(`/admin/providers/${encodeURIComponent(userId)}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function adminTechPresignUpload(payload: { userId: string; keyHint?: string; contentType?: string }) {
  if (!payload?.userId) throw new Error('userId is required')
  return await techRequest('/admin/uploads/presign', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// Financial admin endpoints (stack-level)
export async function adminFinancialGetProvider(userId: string) {
  if (!userId) throw new Error('userId is required')
  return await financialRequest(`/admin/providers/${encodeURIComponent(userId)}`, { method: 'GET' })
}

export async function adminFinancialUpdateProvider(userId: string, payload: Record<string, any>) {
  if (!userId) throw new Error('userId is required')
  return await financialRequest(`/admin/providers/${encodeURIComponent(userId)}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function adminFinancialPresignUpload(payload: { userId: string; keyHint?: string; contentType?: string }) {
  if (!payload?.userId) throw new Error('userId is required')
  return await financialRequest('/admin/uploads/presign', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// Allied admin endpoints (stack-level)
export async function adminAlliedGetProvider(userId: string) {
  if (!userId) throw new Error('userId is required')
  return await alliedRequest(`/admin/providers/${encodeURIComponent(userId)}`, { method: 'GET' })
}

export async function adminAlliedUpdateProvider(userId: string, payload: Record<string, any>) {
  if (!userId) throw new Error('userId is required')
  return await alliedRequest(`/admin/providers/${encodeURIComponent(userId)}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function adminAlliedPresignUpload(payload: { userId: string; keyHint?: string; contentType?: string }) {
  if (!payload?.userId) throw new Error('userId is required')
  return await alliedRequest('/admin/uploads/presign', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// Legal admin endpoints (stack-level)
export async function adminLegalGetProvider(userId: string) {
  if (!userId) throw new Error('userId is required')
  return await legalRequest(`/admin/providers/${encodeURIComponent(userId)}`, { method: 'GET' })
}

export async function adminLegalUpdateProvider(userId: string, payload: Record<string, any>) {
  if (!userId) throw new Error('userId is required')
  return await legalRequest(`/admin/providers/${encodeURIComponent(userId)}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function adminLegalPresignUpload(payload: { userId: string; keyHint?: string; contentType?: string }) {
  if (!payload?.userId) throw new Error('userId is required')
  return await legalRequest('/admin/uploads/presign', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// Admin reviews (stack-level)
export async function adminListProviderReviews(
  profession: ProviderProfession,
  userId: string,
  cursor?: string,
  limit?: number
) {
  if (!userId) throw new Error('userId is required')
  const qp = new URLSearchParams({ userId })
  if (cursor) qp.set('cursor', cursor)
  if (limit) qp.set('limit', String(limit))
  const qs = qp.toString()
  return await stackRequestByProfession(profession)(`/admin/reviews${qs ? `?${qs}` : ''}`, { method: 'GET' })
}

export async function adminCreateProviderReview(profession: ProviderProfession, payload: Record<string, any>) {
  if (!payload?.revieweeId) throw new Error('revieweeId is required')
  if (payload?.rating == null) throw new Error('rating is required')
  return await stackRequestByProfession(profession)('/admin/reviews/create', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function adminUpdateProviderReview(profession: ProviderProfession, payload: Record<string, any>) {
  if (!payload?.reviewId) throw new Error('reviewId is required')
  return await stackRequestByProfession(profession)('/admin/reviews/update', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function adminDeleteProviderReview(profession: ProviderProfession, payload: { reviewId: string }) {
  if (!payload?.reviewId) throw new Error('reviewId is required')
  return await stackRequestByProfession(profession)('/admin/reviews/delete', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// Property admin endpoints (stack-level)
export async function adminPropertyGetProvider(userId: string) {
  if (!userId) throw new Error('userId is required')
  return await propertyRequest(`/admin/providers/${encodeURIComponent(userId)}`, { method: 'GET' })
}

export async function adminPropertyUpdateProvider(userId: string, payload: Record<string, any>) {
  if (!userId) throw new Error('userId is required')
  return await propertyRequest(`/admin/providers/${encodeURIComponent(userId)}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function adminPropertyPresignUpload(payload: { userId: string; keyHint?: string; contentType?: string }) {
  if (!payload?.userId) throw new Error('userId is required')
  return await propertyRequest('/admin/uploads/presign', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}
